import React, { useState } from 'react'
import { toast } from 'react-hot-toast'

export const TestPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [testResults, setTestResults] = useState<string[]>([])

  const addResult = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
    const timestamp = new Date().toLocaleTimeString()
    const result = `[${timestamp}] [${type.toUpperCase()}] ${message}`
    setTestResults(prev => [result, ...prev])
  }

  const testBackendAPI = async () => {
    setLoading(true)
    addResult('开始测试后端API连接...', 'info')

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        addResult('未找到认证令牌，请先登录', 'error')
        return
      }

      // 测试管理员统计API
      addResult('测试管理员统计API...', 'info')
      const statsResponse = await fetch(`${import.meta.env.VITE_API_URL}/admin/dashboard/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (statsResponse.ok) {
        const statsData = await statsResponse.json()
        addResult(`✅ 统计API连接成功，返回数据: ${JSON.stringify(statsData.success)}`, 'success')
      } else {
        addResult(`❌ 统计API连接失败: ${statsResponse.status}`, 'error')
      }

      // 测试投诉API
      addResult('测试投诉处理API...', 'info')
      const complaintsResponse = await fetch(`${import.meta.env.VITE_API_URL}/admin/complaints/stats/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (complaintsResponse.ok) {
        addResult('✅ 投诉API连接成功', 'success')
      } else {
        addResult(`❌ 投诉API连接失败: ${complaintsResponse.status}`, 'error')
      }

      // 测试通知API
      addResult('测试通知管理API...', 'info')
      const notificationsResponse = await fetch(`${import.meta.env.VITE_API_URL}/admin/notifications/stats/overview`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (notificationsResponse.ok) {
        addResult('✅ 通知API连接成功', 'success')
      } else {
        addResult(`❌ 通知API连接失败: ${notificationsResponse.status}`, 'error')
      }

    } catch (error) {
      addResult(`❌ API测试异常: ${error}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const testWebSocket = () => {
    addResult('开始测试WebSocket连接...', 'info')

    try {
      const wsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8788'
      const token = localStorage.getItem('token')

      if (!token) {
        addResult('未找到认证令牌，无法测试WebSocket', 'error')
        return
      }

      const ws = new WebSocket(`${wsUrl}/ws/admin?token=${token}`)

      ws.onopen = () => {
        addResult('✅ WebSocket连接成功', 'success')
        ws.close()
      }

      ws.onerror = (error) => {
        addResult(`❌ WebSocket连接失败: ${error}`, 'error')
      }

      ws.onclose = () => {
        addResult('WebSocket连接已关闭', 'info')
      }

      // 5秒超时
      setTimeout(() => {
        if (ws.readyState === WebSocket.CONNECTING) {
          addResult('❌ WebSocket连接超时', 'error')
          ws.close()
        }
      }, 5000)

    } catch (error) {
      addResult(`❌ WebSocket测试异常: ${error}`, 'error')
    }
  }

  const testCreateComplaint = async () => {
    setLoading(true)
    addResult('开始测试创建投诉...', 'info')

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        addResult('未找到认证令牌，请先登录', 'error')
        return
      }

      // 首先需要创建测试数据
      addResult('创建测试投诉数据...', 'info')

      // 这里应该有创建投诉的API，但当前没有，我们模拟测试
      addResult('⚠️ 投诉创建API暂未实现，跳过测试', 'info')

    } catch (error) {
      addResult(`❌ 创建投诉测试异常: ${error}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const testCreateNotification = async () => {
    setLoading(true)
    addResult('开始测试创建通知...', 'info')

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        addResult('未找到认证令牌，请先登录', 'error')
        return
      }

      const notificationData = {
        title: '测试通知',
        content: '这是一个测试通知，用于验证通知功能是否正常工作。',
        notification_type: 'system',
        target_user_types: ['admin'],
        priority: 'normal'
      }

      addResult('发送通知创建请求...', 'info')
      const response = await fetch(`${import.meta.env.VITE_API_URL}/admin/notifications`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      })

      if (response.ok) {
        const data = await response.json()
        addResult(`✅ 通知创建成功，ID: ${data.data?.id || 'unknown'}`, 'success')
      } else {
        const errorData = await response.json()
        addResult(`❌ 通知创建失败: ${errorData.error?.message || response.statusText}`, 'error')
      }

    } catch (error) {
      addResult(`❌ 创建通知测试异常: ${error}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  const clearResults = () => {
    setTestResults([])
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">功能测试</h1>
        <p className="text-gray-600">测试投诉处理和通知管理功能</p>
      </div>

      {/* 测试按钮 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">测试项目</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            onClick={testBackendAPI}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {loading ? '测试中...' : '测试后端API'}
          </button>
          <button
            onClick={testWebSocket}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            测试WebSocket
          </button>
          <button
            onClick={testCreateComplaint}
            disabled={loading}
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
          >
            {loading ? '测试中...' : '测试创建投诉'}
          </button>
          <button
            onClick={testCreateNotification}
            disabled={loading}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            {loading ? '测试中...' : '测试创建通知'}
          </button>
        </div>
        <div className="mt-4">
          <button
            onClick={clearResults}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            清除结果
          </button>
        </div>
      </div>

      {/* 测试结果 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">测试结果</h2>
        </div>
        <div className="p-6">
          {testResults.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p>暂无测试结果</p>
              <p className="text-sm mt-2">点击上方测试按钮开始测试</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {testResults.map((result, index) => {
                const isSucess = result.includes('✅')
                const isError = result.includes('❌')
                const isInfo = result.includes('⚠️')

                return (
                  <div
                    key={index}
                    className={`p-3 rounded-lg text-sm font-mono ${
                      isSucess ? 'bg-green-50 text-green-800 border border-green-200' :
                      isError ? 'bg-red-50 text-red-800 border border-red-200' :
                      isInfo ? 'bg-yellow-50 text-yellow-800 border border-yellow-200' :
                      'bg-blue-50 text-blue-800 border border-blue-200'
                    }`}
                  >
                    {result}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* 环境信息 */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">环境信息</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">API URL:</span>
            <span className="ml-2 text-gray-900">{import.meta.env.VITE_API_URL}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">WebSocket URL:</span>
            <span className="ml-2 text-gray-900">{import.meta.env.VITE_WS_URL}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">应用名称:</span>
            <span className="ml-2 text-gray-900">{import.meta.env.VITE_APP_NAME}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">应用版本:</span>
            <span className="ml-2 text-gray-900">{import.meta.env.VITE_APP_VERSION}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">WebSocket功能:</span>
            <span className="ml-2 text-gray-900">{import.meta.env.VITE_ENABLE_WEBSOCKET}</span>
          </div>
          <div>
            <span className="font-medium text-gray-700">通知功能:</span>
            <span className="ml-2 text-gray-900">{import.meta.env.VITE_ENABLE_NOTIFICATIONS}</span>
          </div>
        </div>
      </div>

      {/* 功能说明 */}
      <div className="bg-white rounded-lg shadow p-6 mt-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">功能说明</h2>
        <div className="prose prose-sm max-w-none text-gray-600">
          <h3 className="text-base font-medium text-gray-900 mt-4">投诉处理功能</h3>
          <ul>
            <li>✅ 投诉列表查看和筛选</li>
            <li>✅ 投诉详情查看</li>
            <li>✅ 投诉处理和状态更新</li>
            <li>✅ 投诉分配给管理员</li>
            <li>✅ 投诉优先级管理</li>
            <li>✅ 处理记录和日志</li>
          </ul>

          <h3 className="text-base font-medium text-gray-900 mt-4">通知管理功能</h3>
          <ul>
            <li>✅ 系统通知创建和发送</li>
            <li>✅ 通知模板管理</li>
            <li>✅ 通知历史查看</li>
            <li>✅ 通知状态跟踪</li>
            <li>✅ 批量通知功能</li>
            <li>✅ 定时发送支持</li>
          </ul>

          <h3 className="text-base font-medium text-gray-900 mt-4">实时通知功能</h3>
          <ul>
            <li>✅ WebSocket实时连接</li>
            <li>✅ 浏览器原生通知</li>
            <li>✅ 实时消息推送</li>
            <li>✅ 通知中心界面</li>
            <li>✅ 未读计数管理</li>
            <li>✅ 通知设置配置</li>
          </ul>
        </div>
      </div>
    </div>
  )
}